import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Send, Users, MapPin, CheckSquare, UsersRound, Download, RefreshCw, Mail, Bell } from "lucide-react";

const Overlay = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
    <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

export const SendLetterDialog = ({ letter, onClose, onSent }) => {
  const [mode, setMode] = useState("all");
  const [districts, setDistricts] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [selDistricts, setSelDistricts] = useState([]);
  const [committeeId, setCommitteeId] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [memberHits, setMemberHits] = useState([]);
  const [selMembers, setSelMembers] = useState([]);
  const [channels, setChannels] = useState({ inapp: true, email: true });
  const [cover, setCover] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { adminApi.districts().then(setDistricts); adminApi.listCommittees().then(setCommittees).catch(() => {}); }, []);
  useEffect(() => {
    if (mode !== "selected" || !memberQuery) { setMemberHits([]); return; }
    const t = setTimeout(() => adminApi.members({ q: memberQuery, district: "all" }).then((r) => setMemberHits((r || []).slice(0, 20))), 300);
    return () => clearTimeout(t);
  }, [memberQuery, mode]);

  const recipients = useCallback(() => {
    if (mode === "district") return { mode, districts: selDistricts };
    if (mode === "selected") return { mode, member_ids: selMembers.map((m) => m.id) };
    if (mode === "committee") return { mode, committee_id: committeeId };
    return { mode: "all" };
  }, [mode, selDistricts, selMembers, committeeId]);

  const runPreview = async () => {
    setBusy(true);
    try { setPreview(await adminApi.sendPreview(letter.id, { recipients: recipients(), channels })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };

  const confirmSend = async () => {
    setBusy(true);
    try {
      const r = await adminApi.sendLetter(letter.id, { recipients: recipients(), channels, cover_note: cover });
      toast.success(`Sent — ${r.created.inapp} in-app, ${r.created.email} emails queued`);
      onSent && onSent(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  const modes = [
    { k: "all", label: "All members", icon: Users }, { k: "district", label: "By district", icon: MapPin },
    { k: "selected", label: "Selected members", icon: CheckSquare }, { k: "committee", label: "Committee only", icon: UsersRound },
  ];

  return (
    <Overlay onClose={onClose}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-gmoaa-green"><Send size={18} /> Send letter</h3>
        <button onClick={onClose} data-testid="send-close"><X size={18} /></button>
      </div>
      <p className="mb-3 text-xs text-gmoaa-text-muted">{letter.ref_no} · {letter.subject}</p>

      <Label className="mb-1 block text-xs font-medium">Recipients</Label>
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {modes.map((m) => (
          <button key={m.k} data-testid={`send-mode-${m.k}`} onClick={() => { setMode(m.k); setPreview(null); }}
            className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs ${mode === m.k ? "border-gmoaa-green bg-gmoaa-green-light text-gmoaa-green" : "border-gmoaa-border-subtle"}`}>
            <m.icon size={16} /> {m.label}
          </button>
        ))}
      </div>

      {mode === "district" && (
        <div className="mb-3 flex max-h-32 flex-wrap gap-1.5 overflow-auto rounded-lg border border-gmoaa-border-subtle p-2">
          {districts.map((d) => (
            <label key={d.id} className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={selDistricts.includes(d.slug)} onChange={(e) => { setPreview(null); setSelDistricts((s) => e.target.checked ? [...s, d.slug] : s.filter((x) => x !== d.slug)); }} /> {d.name_en}
            </label>
          ))}
        </div>
      )}
      {mode === "committee" && (
        <select data-testid="send-committee" value={committeeId} onChange={(e) => { setCommitteeId(e.target.value); setPreview(null); }} className="mb-3 w-full rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-sm">
          <option value="">Select committee…</option>
          {committees.map((c) => <option key={c.id} value={c.id}>{c.name_en || c.title_en || c.name}</option>)}
        </select>
      )}
      {mode === "selected" && (
        <div className="mb-3">
          <Input data-testid="send-member-search" value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Search members by name…" />
          {memberHits.length > 0 && (
            <div className="mt-1 max-h-32 overflow-auto rounded-lg border border-gmoaa-border-subtle">
              {memberHits.map((m) => (
                <button key={m.id} onClick={() => { setSelMembers((s) => s.find((x) => x.id === m.id) ? s : [...s, m]); setPreview(null); }} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-gmoaa-green-light">
                  {m.name_en} · {m.district_slug} {m.email ? "" : "· (no email)"}
                </button>
              ))}
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-1">
            {selMembers.map((m) => <span key={m.id} className="flex items-center gap-1 rounded-full bg-gmoaa-green-light px-2 py-0.5 text-[11px]">{m.name_en}<button onClick={() => { setSelMembers((s) => s.filter((x) => x.id !== m.id)); setPreview(null); }}><X size={10} /></button></span>)}
          </div>
        </div>
      )}

      <Label className="mb-1 block text-xs font-medium">Delivery channels</Label>
      <div className="mb-3 flex gap-4">
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid="send-ch-inapp" checked={channels.inapp} onChange={(e) => { setChannels((c) => ({ ...c, inapp: e.target.checked })); setPreview(null); }} /> <Bell size={14} /> In-app Notice</label>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid="send-ch-email" checked={channels.email} onChange={(e) => { setChannels((c) => ({ ...c, email: e.target.checked })); setPreview(null); }} /> <Mail size={14} /> Email (PDF attached)</label>
      </div>
      <Label className="mb-1 block text-xs font-medium">Cover note (optional)</Label>
      <textarea data-testid="send-cover" value={cover} onChange={(e) => setCover(e.target.value)} rows={2} className="mb-3 w-full rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-sm" placeholder="Short message added to the email body" />

      {preview && (
        <div data-testid="send-preview-box" className="mb-3 rounded-lg border border-gmoaa-green/30 bg-gmoaa-green-light/40 p-3 text-sm">
          <b>{preview.total}</b> recipients · In-app: <b>{preview.inapp_count}</b> · Emails: <b>{preview.email_count}</b>
          {preview.no_email > 0 && <> · <span className="text-gmoaa-red">{preview.no_email} skipped (no email)</span></>}
          {preview.duplicates_removed > 0 && <> · {preview.duplicates_removed} duplicate email(s) merged</>}
          <div className="mt-1 text-xs text-gmoaa-text-muted">About {preview.estimate_days} day(s) at {preview.per_day}/day.</div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="send-preview-btn">{busy ? "…" : "Preview recipients"}</Button>
        <Button onClick={confirmSend} disabled={busy || !preview} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="send-confirm-btn"><Send size={15} className="mr-1" /> Confirm &amp; Send</Button>
      </div>
    </Overlay>
  );
};

export const SentLog = ({ letter, onClose }) => {
  const [data, setData] = useState({ deliveries: [], summary: {} });
  const [status, setStatus] = useState("all");
  const [district, setDistrict] = useState("all");
  const [districts, setDistricts] = useState([]);

  const load = useCallback(() => { adminApi.deliveries(letter.id, { status, district }).then(setData); }, [letter.id, status, district]);
  useEffect(() => { load(); adminApi.districts().then(setDistricts); }, [load]);

  const resend = async () => { const r = await adminApi.resendFailed(letter.id); toast.success(`Re-queued ${r.requeued} failed`); setTimeout(load, 1500); };
  const csv = async () => { const blob = await adminApi.deliveriesCsv(letter.id); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `sent-log-${(letter.ref_no || "").replace(/\//g, "-")}.csv`; a.click(); };

  const s = data.summary || {};
  const chip = (c) => ({ sent: "bg-green-100 text-green-700", queued: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700", bounced: "bg-red-100 text-red-700" }[c] || "bg-slate-100 text-slate-600");

  return (
    <Overlay onClose={onClose}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-serif text-lg font-semibold text-gmoaa-green">Sent log · {letter.ref_no}</h3>
        <button onClick={onClose} data-testid="sentlog-close"><X size={18} /></button>
      </div>
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        {["total", "sent", "queued", "failed", "read"].map((k) => <span key={k} className="rounded-full bg-gmoaa-surface px-2.5 py-1"><b>{s[k] || 0}</b> {k}</span>)}
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sentlog-status" className="rounded-lg border border-gmoaa-border-subtle px-2 py-1 text-xs">
          <option value="all">All status</option><option value="sent">Sent</option><option value="queued">Queued</option><option value="failed">Failed</option>
        </select>
        <select value={district} onChange={(e) => setDistrict(e.target.value)} className="rounded-lg border border-gmoaa-border-subtle px-2 py-1 text-xs">
          <option value="all">All districts</option>{districts.map((d) => <option key={d.id} value={d.slug}>{d.name_en}</option>)}
        </select>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={resend} data-testid="sentlog-resend"><RefreshCw size={12} className="mr-1" /> Resend to failed</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={csv} data-testid="sentlog-csv"><Download size={12} className="mr-1" /> CSV</Button>
      </div>
      <div className="max-h-80 overflow-auto rounded-lg border border-gmoaa-border-subtle">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gmoaa-green-light text-left text-gmoaa-green"><tr><th className="p-2">Member</th><th className="p-2">District</th><th className="p-2">Channel</th><th className="p-2">Status</th><th className="p-2">Read</th></tr></thead>
          <tbody>
            {data.deliveries.map((d) => (
              <tr key={d.id} className="border-t border-gmoaa-border-subtle">
                <td className="p-2">{d.member_name}</td><td className="p-2">{d.district}</td><td className="p-2">{d.channel}</td>
                <td className="p-2"><span className={`rounded px-1.5 py-0.5 ${chip(d.status)}`}>{d.status}</span>{d.error ? <span className="ml-1 text-[10px] text-gmoaa-red" title={d.error}>⚠</span> : ""}</td>
                <td className="p-2">{d.read_at ? "✓" : "—"}</td>
              </tr>
            ))}
            {data.deliveries.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gmoaa-text-muted">No records.</td></tr>}
          </tbody>
        </table>
      </div>
    </Overlay>
  );
};
